//src/components/property/PropertyCard.jsx
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaBath,
  FaBed,
  FaBuilding,
  FaChevronLeft,
  FaChevronRight,
  FaCircle,
  FaHardHat,
  FaHeart,
  FaMapMarkerAlt,
  FaRegHeart,
  FaRulerCombined,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { formatArea } from '../../helpers/formatArea';
import { formatPrice } from '../../helpers/formatPrice';
import { getImageUrl } from '../../utils/imageUrl';
import { isAuthenticated } from '../../utils/auth';
import { getPropertyPath } from '../../utils/propertyUrl';

const AUTOPLAY_DELAY = 3500;

function normalizeImage(input, fallbackAlt) {
    if (!input) return null;

    if (typeof input === 'string') {
        return {
            url: getImageUrl(input),
            alt: fallbackAlt,
        };
    }

    if (typeof input === 'object') {
        const source = input.url || input.image_url || input.path || input.src;
        if (!source) return null;

        return {
            url: getImageUrl(source),
            alt: input.alt || fallbackAlt,
        };
    }

    return null;
}

function formatCompactPrice(price) {
    const num = Number(price);
    if (!price || Number.isNaN(num)) return null;

    if (num >= 1_000_000_000) {
        const miliar = num / 1_000_000_000;
        const formatted =
            miliar % 1 === 0
                ? miliar.toLocaleString('id-ID')
                : miliar.toLocaleString('id-ID', { maximumFractionDigits: 1 });
        return `Rp ${formatted} M`;
    }

    if (num >= 1_000_000) {
        const juta = num / 1_000_000;
        const formatted =
            juta % 1 === 0 ? juta.toLocaleString('id-ID') : juta.toLocaleString('id-ID', { maximumFractionDigits: 1 });
        return `Rp ${formatted} Jt`;
    }

    return `Rp ${num.toLocaleString('id-ID')}`;
}

const PropertyCard = ({ property, isFavorite: isFavoriteProp, onToggleFavorite }) => {
    const navigate = useNavigate();

    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [localFavorite, setLocalFavorite] = useState(Boolean(property?.is_favorite ?? property?.favorite ?? false));

    const propertyUrl = useMemo(() => getPropertyPath(property?.slug), [property?.slug]);

    const isFavorite = typeof isFavoriteProp === 'boolean' ? isFavoriteProp : localFavorite;

    const isInspected = String(property?.inspection_status || '').toLowerCase() === 'completed';

    const images = useMemo(() => {
        const rawImages = Array.isArray(property?.images) ? property.images : [];
        const fallbackAlt = property?.title || 'Property image';

        const normalized = rawImages.map((item) => normalizeImage(item, fallbackAlt)).filter(Boolean);

        const fallbackImage = property?.main_image_url || property?.image_url || property?.og_image_url || null;

        if (!normalized.length && fallbackImage) {
            normalized.push({
                url: getImageUrl(fallbackImage),
                alt: fallbackAlt,
            });
        }

        if (!normalized.length) {
            normalized.push({
                url: '/images/placeholder-property.jpg',
                alt: fallbackAlt,
            });
        }

        return normalized;
    }, [property?.images, property?.main_image_url, property?.image_url, property?.og_image_url, property?.title]);

    useEffect(() => {
        setActiveIndex(0);
    }, [property?.slug, property?.id]);

    useEffect(() => {
        if (typeof isFavoriteProp === 'boolean') return;
        setLocalFavorite(Boolean(property?.is_favorite ?? property?.favorite ?? false));
    }, [property?.is_favorite, property?.favorite, isFavoriteProp, property?.id]);

    useEffect(() => {
        if (images.length <= 1 || isPaused) return undefined;

        const timer = window.setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % images.length);
        }, AUTOPLAY_DELAY);

        return () => window.clearInterval(timer);
    }, [images.length, isPaused]);

    const goPrev = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
        },
        [images.length]
    );

    const goNext = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveIndex((prev) => (prev + 1) % images.length);
        },
        [images.length]
    );

    const handleFavoriteToggle = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (!isAuthenticated()) {
                window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
                return;
            }

            const nextValue = !isFavorite;

            if (typeof onToggleFavorite === 'function') {
                onToggleFavorite(property, nextValue);
                return;
            }

            setLocalFavorite(nextValue);
        },
        [isFavorite, onToggleFavorite, property]
    );

    const handleCardClick = useCallback(() => {
        navigate(propertyUrl || '/properties');
    }, [navigate, propertyUrl]);

    const handleCardKeyDown = useCallback(
        (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(propertyUrl || '/properties');
            }
        },
        [navigate, propertyUrl]
    );

    const priceText = formatCompactPrice(property?.price) || property?.price_format || formatPrice(property?.price);

    const title = property?.title || 'Properti';
    const locationText = [property?.city, property?.province].filter(Boolean).join(', ');

    return (
        <article
            className="property-card"
            role="link"
            tabIndex={0}
            onClick={handleCardClick}
            onKeyDown={handleCardKeyDown}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            aria-label={`Lihat detail properti ${title}`}
        >
            <div className="property-media">
                <div
                    className="property-slider"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                >
                    <div
                        className="property-slider-track"
                        style={{
                            transform: `translate3d(-${activeIndex * 100}%,0,0)`,
                        }}
                    >
                        {images.map((image, index) => (
                            <div className="property-slider-slide">
                                <img src={image.url} alt={image.alt || title} className="property-image" />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="property-badges">
                    <div className="property-badges__stack">
                        <div className={`property-status ${property?.status === 'for_rent' ? 'rent' : 'sale'}`}>
                            {property?.status_label || (property?.status === 'for_rent' ? 'Sewa' : 'Dijual')}
                        </div>

                        {isInspected && (
                            <div className="property-inspected-badge">
                                <FaHardHat aria-hidden="true" />
                                <span>Terinspeksi</span>
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        className={`property-favorite-btn ${isFavorite ? 'is-favorite' : ''}`}
                        onClick={handleFavoriteToggle}
                        aria-label={isFavorite ? 'Hapus dari favorit' : 'Simpan ke favorit'}
                        title={isFavorite ? 'Hapus dari favorit' : 'Simpan ke favorit'}
                    >
                        {isFavorite ? <FaHeart aria-hidden="true" /> : <FaRegHeart aria-hidden="true" />}
                    </button>
                </div>

                {images.length > 1 && (
                    <>
                        <button
                            type="button"
                            className="property-slider-btn prev"
                            onClick={goPrev}
                            aria-label="Gambar sebelumnya"
                        >
                            <FaChevronLeft aria-hidden="true" />
                        </button>

                        <button
                            type="button"
                            className="property-slider-btn next"
                            onClick={goNext}
                            aria-label="Gambar selanjutnya"
                        >
                            <FaChevronRight aria-hidden="true" />
                        </button>

                        <div className="property-slider-indicators" aria-hidden="true" role="presentation">
                            {images.map((_, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    className={`property-slider-indicator ${index === activeIndex ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setActiveIndex(index);
                                    }}
                                    aria-label={`Ke gambar ${index + 1}`}
                                >
                                    <FaCircle aria-hidden="true" />
                                </button>
                            ))}
                        </div>

                        <div className="property-image-count property-image-count-bottom">
                            {activeIndex + 1}/{images.length}
                        </div>
                    </>
                )}
            </div>

            <div className="property-card-body">
                <div className="property-price-row">
                    <span className="property-price">{priceText}</span>
                    <span className="property-type-pill">
                        {property?.property_type || property?.type || 'Properti'}
                    </span>
                </div>

                <h3 className="property-title">{title}</h3>

                <p className="property-location" title={locationText || 'Lokasi tidak tersedia'}>
                    <FaMapMarkerAlt aria-hidden="true" />
                    <span>{locationText || 'Lokasi tidak tersedia'}</span>
                </p>

                <div className="property-specs" aria-label="Spesifikasi properti">
                    <span className="property-spec-item">
                        <FaBed className="spec-icon" aria-hidden="true" />
                        <span className="spec-value">
                            {property?.bedrooms || 0}
                            <span className="spec-label"> KT</span>
                        </span>
                    </span>

                    <span className="property-spec-item">
                        <FaBath className="spec-icon" aria-hidden="true" />
                        <span className="spec-value">
                            {property?.bathrooms || 0}
                            <span className="spec-label"> KM</span>
                        </span>
                    </span>

                    <span className="property-spec-item">
                        <FaRulerCombined className="spec-icon" aria-hidden="true" />
                        <span className="spec-value">
                            {formatArea(property?.land_area)}
                            <span className="spec-label"> m²</span>
                        </span>
                    </span>

                    <span className="property-spec-item">
                        <FaBuilding className="spec-icon" aria-hidden="true" />
                        <span className="spec-value">
                            {formatArea(property?.building_area)}
                            <span className="spec-label"> m²</span>
                        </span>
                    </span>
                </div>
            </div>
        </article>
    );
};

export default memo(PropertyCard);
